import React, { useEffect, useState } from 'react';
import { useGoalStore } from '../../../stores/goalStore';
import { useTaskStore } from '../../../stores/taskStore';
import { useWorkspaceStore } from '../../../stores/workspaceStore';
import { useAuthStore } from '../../../stores/authStore';
import type { Goal, CreateGoalRequest, UpdateGoalRequest, Task, CreateTaskRequest } from '../../../types';

interface GoalDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  goalId: string | 'new';
  parentGoalId?: string;
}

export const GoalDetailModal: React.FC<GoalDetailModalProps> = ({
  isOpen,
  onClose,
  goalId,
  parentGoalId
}) => {
  const { user } = useAuthStore();
  const { currentWorkspace } = useWorkspaceStore();
  const {
    loading: goalLoading,
    error: goalError,
    createGoal,
    updateGoal,
    deleteGoal,
    getGoal,
    getRootGoals,
    clearError: clearGoalError
  } = useGoalStore();

  const {
    createTask,
    updateTask,
    deleteTask,
    taskTypes,
    loading: taskLoading
  } = useTaskStore();

  // 组件状态
  const [goal, setGoal] = useState<Goal | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  // 表单数据
  const [formData, setFormData] = useState<CreateGoalRequest>({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    teamId: currentWorkspace.type === 'team' ? currentWorkspace.teamId : undefined,
    parentGoalId: parentGoalId
  });

  const [taskFormData, setTaskFormData] = useState<CreateTaskRequest>({
    title: '',
    description: '',
    size: 'MEDIUM',
    priority: 'MEDIUM',
    taskTypeId: undefined,
    goalId: goalId !== 'new' ? goalId : undefined
  });

  // 加载目标详情
  useEffect(() => {
    if (isOpen && goalId !== 'new') {
      loadGoalDetail();
    } else if (isOpen && goalId === 'new') {
      setIsEditing(true);
      resetGoalForm();
    }
  }, [isOpen, goalId]);

  // 初始化表单默认日期
  useEffect(() => {
    if (goalId === 'new') {
      const today = new Date();
      const nextMonth = new Date();
      nextMonth.setMonth(today.getMonth() + 1);

      setFormData(prev => ({
        ...prev,
        startDate: today.toISOString().split('T')[0],
        endDate: nextMonth.toISOString().split('T')[0]
      }));
    }
  }, [goalId]);

  // 处理键盘按键
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showTaskForm || editingTask) {
          handleCancelTaskEdit();
        } else if (isEditing) {
          handleCancelGoalEdit();
        } else {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, showTaskForm, editingTask, isEditing, onClose]);

  const loadGoalDetail = async () => {
    try {
      const goalDetail = await getGoal(goalId as string);
      if (goalDetail) {
        setGoal(goalDetail);
        setTasks(goalDetail.tasks || []);
        setFormData({
          title: goalDetail.title,
          description: goalDetail.description || '',
          startDate: goalDetail.startDate.split('T')[0],
          endDate: goalDetail.endDate.split('T')[0],
          teamId: goalDetail.teamId || undefined,
          parentGoalId: goalDetail.parentGoalId || undefined
        });
      }
    } catch (error) {
      console.error('加载目标详情失败:', error);
    }
  };

  const resetGoalForm = () => {
    const today = new Date();
    const nextMonth = new Date();
    nextMonth.setMonth(today.getMonth() + 1);

    setFormData({
      title: '',
      description: '',
      startDate: today.toISOString().split('T')[0],
      endDate: nextMonth.toISOString().split('T')[0],
      teamId: currentWorkspace.type === 'team' ? currentWorkspace.teamId : undefined,
      parentGoalId: parentGoalId
    });
  };

  const resetTaskForm = () => {
    setTaskFormData({
      title: '',
      description: '',
      size: 'MEDIUM',
      priority: 'MEDIUM',
      taskTypeId: taskTypes.length > 0 ? taskTypes[0].id : undefined,
      goalId: goalId !== 'new' ? goalId : goal?.id
    });
  };

  // 保存目标
  const handleSaveGoal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) return;

    try {
      if (goalId === 'new') {
        const newGoal = await createGoal(formData);
        setGoal(newGoal);
        setIsEditing(false);
        onClose(); // 创建成功后关闭窗口
      } else {
        const updateData: UpdateGoalRequest = {
          title: formData.title,
          description: formData.description,
          startDate: formData.startDate,
          endDate: formData.endDate
        };
        await updateGoal(goal!.id, updateData);
        await loadGoalDetail();
        setIsEditing(false);
      }
    } catch (error) {
      // 错误已在store中处理
    }
  };

  // 取消编辑目标
  const handleCancelGoalEdit = () => {
    if (goalId === 'new') {
      onClose();
    } else {
      setIsEditing(false);
      if (goal) {
        setFormData({
          title: goal.title,
          description: goal.description || '',
          startDate: goal.startDate.split('T')[0],
          endDate: goal.endDate.split('T')[0],
          teamId: goal.teamId || undefined,
          parentGoalId: goal.parentGoalId || undefined
        });
      }
    }
  };

  // 删除目标
  const handleDeleteGoal = async () => {
    if (!goal || !confirm(`确定要删除目标"${goal.title}"吗？`)) return;

    try {
      await deleteGoal(goal.id);
      onClose();
    } catch (error) {
      // 错误已在store中处理
    }
  };

  // 切换目标状态
  const handleToggleGoalStatus = async () => {
    if (!goal) return;

    try {
      const newStatus = goal.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
      await updateGoal(goal.id, { status: newStatus });
      await loadGoalDetail();
    } catch (error) {
      // 错误已在store中处理
    }
  };

  // 保存任务
  const handleSaveTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!taskFormData.title.trim()) return;
    if (!taskFormData.taskTypeId) {
      alert('请选择任务类型');
      return;
    }

    try {
      if (editingTask) {
        await updateTask(editingTask.id, {
          title: taskFormData.title,
          description: taskFormData.description,
          size: taskFormData.size,
          priority: taskFormData.priority,
          taskTypeId: taskFormData.taskTypeId
        });
      } else {
        await createTask(taskFormData);
      }
      await loadGoalDetail();
      handleCancelTaskEdit();
    } catch (error) {
      // 错误已在store中处理
    }
  };

  // 开始编辑任务
  const handleStartEditTask = (task: Task) => {
    setEditingTask(task);
    setTaskFormData({
      title: task.title,
      description: task.description || '',
      size: task.size,
      priority: task.priority,
      taskTypeId: task.taskTypeId,
      goalId: task.goalId || undefined
    });
  };

  // 取消编辑任务
  const handleCancelTaskEdit = () => {
    setShowTaskForm(false);
    setEditingTask(null);
    resetTaskForm();
  };

  // 删除任务
  const handleDeleteTask = async (task: Task) => {
    if (!confirm(`确定要删除任务"${task.title}"吗？`)) return;

    try {
      await deleteTask(task.id);
      await loadGoalDetail();
    } catch (error) {
      // 错误已在store中处理
    }
  };

  if (!isOpen) return null;

  const isOwner = goal?.ownerId === user?.id || goalId === 'new';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-neutral-25 rounded-xl shadow-lg max-w-2xl w-full max-h-[85vh] flex flex-col border border-neutral-100">
        {/* 头部 */}
        <div className="flex items-center justify-between p-5 border-b border-neutral-100">
          <h2 className="text-lg font-medium text-gray-700">
            {goalId === 'new' ? '创建目标' : isEditing ? '编辑目标' : goal?.title || '目标详情'}
          </h2>
          <div className="flex items-center gap-2">
            {goal && !isEditing && isOwner && (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-3 py-1 text-sm bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                >
                  编辑
                </button>
                {goal.status !== 'COMPLETED' && (
                  <button
                    onClick={handleToggleGoalStatus}
                    className={`px-3 py-1 text-sm rounded transition-colors ${
                      goal.status === 'ACTIVE'
                        ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                        : 'bg-emerald-500 text-white hover:bg-emerald-600'
                    }`}
                  >
                    {goal.status === 'ACTIVE' ? '暂停' : '开始'}
                  </button>
                )}
              </>
            )}
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-neutral-100 rounded transition-colors"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>

        {/* 错误提示 */}
        {goalError && (
          <div className="p-3 bg-red-50 border-b border-red-200 flex items-center justify-between">
            <span className="text-red-700 text-sm">{goalError}</span>
            <button
              onClick={clearGoalError}
              className="text-red-500 hover:text-red-700 font-semibold"
            >
              ×
            </button>
          </div>
        )}

        {/* 内容区域 */}
        <div className="flex-1 overflow-y-auto p-5">
          {(isEditing || goalId === 'new') ? (
            /* 目标编辑表单 */
            <form onSubmit={handleSaveGoal} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">标题</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-neutral-25"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">描述</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent resize-none bg-neutral-25"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">开始日期</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-neutral-25"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">结束日期</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-neutral-25"
                    required
                  />
                </div>
              </div>

              {/* 父目标选择 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">父目标</label>
                <select
                  value={formData.parentGoalId || ''}
                  onChange={(e) => setFormData({ ...formData, parentGoalId: e.target.value || undefined })}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-neutral-25"
                >
                  <option value="">无父目标（根目标）</option>
                  {getRootGoals().map(goal => (
                    <option key={goal.id} value={goal.id}>
                      {goal.title}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  disabled={goalLoading.create || goalLoading.update}
                >
                  {goalId === 'new' ? '创建' : '保存'}
                </button>
                <button
                  type="button"
                  onClick={handleCancelGoalEdit}
                  className="px-4 py-2 bg-neutral-200 text-gray-600 rounded hover:bg-neutral-300 transition-colors"
                >
                  取消
                </button>
                {goal && goalId !== 'new' && isOwner && (
                  <button
                    type="button"
                    onClick={handleDeleteGoal}
                    className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors ml-auto"
                  >
                    删除目标
                  </button>
                )}
              </div>
            </form>
          ) : goalId !== 'new' && goal ? (
            /* 目标详情显示 */
            <div className="space-y-6">
              {/* 目标信息 */}
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-3 h-3 rounded-full ${
                    goal.status === 'ACTIVE' ? 'bg-blue-500 animate-pulse' :
                    goal.status === 'COMPLETED' ? 'bg-green-500' :
                    goal.status === 'PAUSED' ? 'bg-yellow-500' : 'bg-red-500'
                  }`} />
                  <span className="text-sm text-gray-600">
                    {goal.status === 'ACTIVE' ? '进行中' :
                     goal.status === 'COMPLETED' ? '已完成' :
                     goal.status === 'PAUSED' ? '暂停' : '已取消'}
                  </span>
                  <span className="text-sm text-blue-600 font-medium">{goal.progress}% 完成</span>
                </div>

                {goal.description && (
                  <p className="text-sm text-gray-700 mb-4">{goal.description}</p>
                )}

                <div className="text-sm text-gray-600 mb-4">
                  {new Date(goal.startDate).toLocaleDateString('zh-CN')} ~ {new Date(goal.endDate).toLocaleDateString('zh-CN')}
                </div>

                {/* 进度条 */}
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${
                      goal.status === 'ACTIVE' ? 'bg-blue-500' : 'bg-gray-400'
                    }`}
                    style={{ width: `${goal.progress}%` }}
                  />
                </div>
              </div>

              {/* 任务列表 */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-medium text-gray-800">关联任务 ({tasks.length})</h3>
                  {isOwner && (
                    <button
                      onClick={() => {
                        setEditingTask(null);
                        resetTaskForm();
                        setShowTaskForm(true);
                      }}
                      className="px-3 py-1 text-sm bg-emerald-500 text-white rounded hover:bg-emerald-600 transition-colors"
                    >
                      添加任务
                    </button>
                  )}
                </div>

                {/* 任务表单 */}
                {(showTaskForm || editingTask) && (
                  <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4 mb-4">
                    <h4 className="text-base font-medium text-gray-800 mb-3">
                      {editingTask ? '编辑任务' : '添加任务'}
                    </h4>
                    <form onSubmit={handleSaveTask} className="space-y-3">
                      <div>
                        <input
                          type="text"
                          placeholder="任务标题"
                          value={taskFormData.title}
                          onChange={(e) => setTaskFormData({ ...taskFormData, title: e.target.value })}
                          className="w-full px-3 py-2 border border-neutral-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-white"
                          required
                        />
                      </div>
                      <div>
                        <textarea
                          placeholder="任务描述（可选）"
                          value={taskFormData.description}
                          onChange={(e) => setTaskFormData({ ...taskFormData, description: e.target.value })}
                          rows={2}
                          className="w-full px-3 py-2 border border-neutral-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent resize-none bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">任务类型 *</label>
                        <select
                          value={taskFormData.taskTypeId || ''}
                          onChange={(e) => setTaskFormData({ ...taskFormData, taskTypeId: e.target.value || undefined })}
                          className="w-full px-2 py-1 border border-neutral-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-white"
                          required
                        >
                          <option value="">请选择任务类型</option>
                          {taskTypes.map(type => (
                            <option key={type.id} value={type.id}>
                              {type.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">任务大小</label>
                          <select
                            value={taskFormData.size}
                            onChange={(e) => setTaskFormData({ ...taskFormData, size: e.target.value as any })}
                            className="w-full px-2 py-1 border border-neutral-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-white"
                          >
                            <option value="SMALL">小</option>
                            <option value="MEDIUM">中</option>
                            <option value="LARGE">大</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">优先级</label>
                          <select
                            value={taskFormData.priority}
                            onChange={(e) => setTaskFormData({ ...taskFormData, priority: e.target.value as any })}
                            className="w-full px-2 py-1 border border-neutral-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-white"
                          >
                            <option value="LOW">低</option>
                            <option value="MEDIUM">中</option>
                            <option value="HIGH">高</option>
                          </select>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="submit"
                          className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                          disabled={taskLoading.create || taskLoading.update}
                        >
                          {editingTask ? '保存' : '添加'}
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelTaskEdit}
                          className="px-3 py-1 bg-neutral-200 text-gray-600 text-sm rounded hover:bg-neutral-300 transition-colors"
                        >
                          取消
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* 任务列表 */}
                <div className="space-y-2">
                  {tasks.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      {isOwner ? '还没有任务，点击"添加任务"开始' : '暂无关联任务'}
                    </div>
                  ) : (
                    tasks.map(task => (
                      <div key={task.id} className="flex items-center gap-3 p-3 bg-neutral-50 rounded-lg group">
                        <div className={`w-2 h-2 rounded-full ${
                          task.status === 'DONE' ? 'bg-green-500' :
                          task.status === 'IN_PROGRESS' ? 'bg-blue-500' : 'bg-gray-400'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-800">{task.title}</div>
                          {task.description && (
                            <div className="text-xs text-gray-600 mt-1 line-clamp-2">{task.description}</div>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            {task.taskType && (
                              <span
                                className="text-xs px-2 py-1 rounded-full"
                                style={{
                                  backgroundColor: `${task.taskType.color}15`,
                                  color: task.taskType.color,
                                  borderColor: `${task.taskType.color}30`,
                                  border: '1px solid'
                                }}
                              >
                                {task.taskType.name}
                              </span>
                            )}
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              task.size === 'LARGE' ? 'bg-red-100 text-red-700' :
                              task.size === 'MEDIUM' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
                            }`}>
                              {task.size === 'LARGE' ? '大' : task.size === 'MEDIUM' ? '中' : '小'}
                            </span>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              task.priority === 'HIGH' ? 'bg-red-100 text-red-700' :
                              task.priority === 'MEDIUM' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'
                            }`}>
                              {task.priority === 'HIGH' ? '高' : task.priority === 'MEDIUM' ? '中' : '低'}
                            </span>
                            <span className="text-xs text-gray-500">
                              {task.status === 'DONE' ? '已完成' :
                               task.status === 'IN_PROGRESS' ? '进行中' : '待办'}
                            </span>
                          </div>
                        </div>
                        {isOwner && (
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => handleStartEditTask(task)}
                              className="p-1 text-gray-500 hover:text-gray-700 hover:bg-neutral-100 rounded transition-colors"
                              title="编辑任务"
                            >
                              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteTask(task)}
                              className="p-1 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="删除任务"
                            >
                              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          ) : goalId !== 'new' ? (
            /* 加载状态 - 只有在加载现有目标时才显示 */
            <div className="flex flex-col items-center justify-center py-12 space-y-3">
              <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-gray-600">加载目标详情中...</span>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};